import React, { useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { FilterBar } from '../FilterBar';
import styles from './DocumentsTab.module.css';
import { useLocale } from '../../context/DynamicLocaleProvider';
import { useToast } from '../../context/ToastProvider';
import { useUser } from '../../context/UserContext';
import { useProjectDocuments, ProjectDocument } from '../../hooks/useProjectDocuments';
import { getStorage, ref, uploadBytes, getDownloadURL } from 'firebase/storage';
import GlobalEmptyState from '../GlobalEmptyState';
import DoubleConfirmationModal from '../DoubleConfirmationModal';
import { doc, deleteDoc } from 'firebase/firestore';
import { db } from '../../lib/firebase';
import { DocumentTile, ProjectDocumentData } from '../DocumentTile';

interface DocumentsTabProps {
  projectId: string;
  onExport?: () => void;
  onAddDocument?: () => void;
}

export const DocumentsTab: React.FC<DocumentsTabProps> = ({ projectId, onExport, onAddDocument }) => {
  const { t } = useLocale();
  const showToast = useToast();
  const { user } = useUser();
  const navigate = useNavigate();
  const { documents, loading } = useProjectDocuments(projectId);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [isUploading, setIsUploading] = useState(false);
  const [searchValue, setSearchValue] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [documentToDelete, setDocumentToDelete] = useState<ProjectDocument | null>(null);
  const [isDeleting, setIsDeleting] = useState(false);

  const handleFileInputChange = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file || !user) return;

    // Validate file type
    const allowedTypes = ['application/pdf', 'text/csv', 'image/jpeg', 'image/png'];
    if (!allowedTypes.includes(file.type)) {
      showToast(t('project_tab_invalid_file_type') || 'Invalid file type', 'error');
      return;
    }

    // Validate file size (max 24MB)
    const maxSize = 24 * 1024 * 1024;
    if (file.size > maxSize) {
      showToast(t('project_tab_file_too_large') || 'File size must be less than 24MB', 'error');
      return;
    }

    try {
      setIsUploading(true);
      showToast(t('project_tab_urs_uploading') || 'Uploading document...', 'info');

      // Sanitize filename
      const sanitizedFileName = file.name
        .replace(/[^a-zA-Z0-9.-]/g, '_')
        .replace(/\s+/g, '_')
        .substring(0, 100);

      // Upload file to Firebase Storage
      const storage = getStorage();
      const documentId = `${Date.now()}_${sanitizedFileName}`;
      const fileName = `projects/${projectId}/urs/${documentId}`;
      const storageRef = ref(storage, fileName);

      await uploadBytes(storageRef, file);
      const downloadURL = await getDownloadURL(storageRef);

      showToast(t('project_tab_urs_processing') || 'Processing document with AI...', 'info');

      // Call the cloud function
      const response = await fetch('https://addursfromdocument-3l53kqimxq-ey.a.run.app', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          fileUrl: downloadURL,
          projectId: projectId,
          userId: user.uid,
          language: 'en',
        }),
      });

      if (!response.ok) {
        let errorData: any = {};
        try {
          errorData = await response.json();
        } catch (e) {
          const text = await response.text();
          throw new Error(`HTTP error! status: ${response.status}, body: ${text}`);
        }
        throw new Error(errorData.error || `HTTP error! status: ${response.status}`);
      }

      const result = await response.json();

      if (result.success && result.ursCount > 0) {
        showToast(
          t('project_tab_urs_extracted') || `Successfully extracted ${result.ursCount} URS`,
          'success'
        );
      } else {
        showToast(
          t('project_tab_urs_no_requirements') || 'No URS found in document',
          'warning'
        );
      }
    } catch (error) {
      console.error('Error uploading document:', error);
      showToast(
        t('project_tab_upload_error') || 'Failed to upload document',
        'error'
      );
    } finally {
      setIsUploading(false);
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  };

  const handleDeleteClick = (documentOrId: ProjectDocument | string) => {
    const doc = typeof documentOrId === 'string' 
      ? documents.find(d => d.id === documentOrId)
      : documentOrId;
    if (doc) {
      setDocumentToDelete(doc);
      setShowDeleteModal(true);
    }
  };

  const handleDeleteConfirm = async () => {
    if (!documentToDelete) return;

    setIsDeleting(true);
    try {
      // Delete from Firestore
      const docRef = doc(db, 'projects', projectId, 'documents', documentToDelete.id);
      await deleteDoc(docRef);

      // TODO: Optionally delete from Storage as well
      // This would require a cloud function or admin SDK

      showToast(t('project_tab_delete_success') || 'Document deleted successfully', 'success');
      setShowDeleteModal(false);
      setDocumentToDelete(null);
    } catch (error) {
      console.error('Error deleting document:', error);
      showToast(t('project_tab_delete_error') || 'Failed to delete document', 'error');
    } finally {
      setIsDeleting(false);
    }
  };

  const handleDeleteCancel = () => {
    setShowDeleteModal(false);
    setDocumentToDelete(null);
  };

  const filteredDocuments = documents.filter((doc) => {
    const matchesSearch =
      searchValue === '' ||
      doc.filename.toLowerCase().includes(searchValue.toLowerCase());
    const matchesStatus = statusFilter === '' || doc.status === statusFilter;
    return matchesSearch && matchesStatus;
  });

  const convertToProjectDocumentData = (doc: ProjectDocument): ProjectDocumentData => {
    return {
      id: doc.id,
      documentId: doc.documentId,
      filename: doc.filename,
      fileUrl: doc.fileUrl,
      storagePath: doc.storagePath,
      contentType: doc.contentType,
      status: doc.status,
      createdAt: doc.createdAt,
      projectId: doc.projectId,
      userId: doc.userId,
      ursExtracted: doc.ursExtracted,
      ursCount: doc.ursCount,
      fsExtracted: doc.fsExtracted,
      fsCount: doc.fsCount,
      testExtracted: doc.testExtracted,
      testCount: doc.testCount,
      riskExtracted: doc.riskExtracted,
      riskCount: doc.riskCount,
      sopExtracted: doc.sopExtracted,
      sopCount: doc.sopCount,
      embeddingEnabled: doc.embeddingEnabled,
      embeddingSucceeded: doc.embeddingSucceeded,
      chunksCreated: doc.chunksCreated,
      embeddingError: doc.embeddingError,
    };
  };

  return (
    <div className={styles.documentsTab}>
      {/* Header */}
      <div className={styles.tabHeader}>
        <h2 className={styles.tabTitle}>
          {t('project_page_tab_documents') || 'Dokumente'}
        </h2>
      </div>

      {/* FilterBar */}
      <FilterBar
        filters={[
          {
            id: 'status',
            label: t('project_tab_status') || 'Status',
            options: [
              { value: '', label: t('project_tab_all') || 'All' },
              { value: 'processing', label: t('project_tab_status_processing') || 'Processing' },
              { value: 'completed', label: t('project_tab_status_completed') || 'Completed' },
            ],
            value: statusFilter,
            onChange: (value) => setStatusFilter(value),
          },
        ]}
        searchValue={searchValue}
        onSearchChange={setSearchValue}
        searchPlaceholder={t('project_tab_search_documents') || 'Search documents...'}
        showInviteButton={true}
        onAddUser={onAddDocument || (() => fileInputRef.current?.click())}
        addButtonText={t('project_tab_add_document') || 'Add a document'}
      />

      {/* Upload Input (hidden) */}
      <input
        ref={fileInputRef}
        type="file"
        accept=".pdf,.csv,.doc,.docx,image/jpeg,image/png"
        onChange={handleFileInputChange}
        style={{ display: 'none' }}
        disabled={isUploading}
      />

      {/* Documents Grid */}
      {loading ? (
        <div className={styles.loading}>Loading documents...</div>
      ) : filteredDocuments.length === 0 ? (
        <GlobalEmptyState />
      ) : (
        <div className={styles.documentsGrid}>
          {filteredDocuments.map((document) => (
            <DocumentTile
              key={document.id}
              document={convertToProjectDocumentData(document)}
              variant="tile"
              isProjectDocument={true}
              onClick={(id) => {
                // Navigate to document view with projectId query param
                navigate(`/document/${id}?projectId=${projectId}`);
              }}
              onView={(id, fileUrl) => {
                // Navigate to document view instead of opening in new tab
                navigate(`/document/${id}?projectId=${projectId}`);
              }}
              onDelete={handleDeleteClick}
            />
          ))}
        </div>
      )}

      {/* Delete Confirmation Modal */}
      <DoubleConfirmationModal
        isOpen={showDeleteModal}
        title={t('project_tab_delete_document') || 'Delete Document'}
        paragraph={
          documentToDelete
            ? t('project_tab_delete_document_confirm') ||
              `Are you sure you want to delete "${documentToDelete.filename}"? This action cannot be undone.`
            : ''
        }
        primaryLabel={t('project_tab_delete') || 'Delete'}
        primaryIcon="/assets/icons/trash.red.svg"
        secondaryLabel={t('project_tab_cancel') || 'Cancel'}
        secondaryIcon="/assets/icons/xmark.svg"
        onPrimary={handleDeleteConfirm}
        onSecondary={handleDeleteCancel}
        primaryDestructive
        loading={isDeleting}
      />
    </div>
  );
};
